import type { DivisionJob } from '../hooks/useSchedulerGeneration'

/**
 * The jobs belonging to the attempt currently being watched.
 *
 * A selective generation deletes jobs only for the divisions it selected, so a
 * flight accumulates rows from earlier, wider attempts. Counting all of them as
 * the current attempt is what produced "11 divisions couldn't be scheduled"
 * after a one-division generate — ten of those divisions had not been
 * attempted at all.
 *
 * `is_current_attempt` is the backend's authority (derived from the flight's
 * current `generation_payload`). It is deliberately treated as current when
 * ABSENT: an older API, or a flight with no attempt scope to compare against,
 * must behave exactly as it did before this existed rather than silently
 * hiding every division.
 */
export function currentAttemptJobs(
  jobs: readonly DivisionJob[] | undefined | null
): DivisionJob[] {
  const all = jobs ?? []
  const scoped = all.filter((job) => job.is_current_attempt !== false)

  // If the flag excluded everything, the scope is not trustworthy for display
  // — fall back rather than render an empty progress screen.
  return scoped.length > 0 ? scoped : [...all]
}

/** Jobs kept from an earlier attempt, if a caller wants to surface them apart. */
export function retainedJobs(
  jobs: readonly DivisionJob[] | undefined | null
): DivisionJob[] {
  return (jobs ?? []).filter((job) => job.is_current_attempt === false)
}
